import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Button, Step, StepButton, StepContent, Stepper } from '@mui/material';
import { FormContainer } from 'components';
import { discounts, DiscountTypes, ShopTypes } from 'api';
import { useApiRequest, useFiltersSearchParams } from 'hooks';
import { getDiffFields, getFieldsWithValues } from 'helpers/getFormFields';
import { appLinks } from 'routes/links';
import { useStoreContext } from 'store';
import { useQuery } from '@tanstack/react-query';
import GeneralSettings from './components/GeneralSettings';
import {
  checkExternalDiscount,
  dataInitialSelectsOptions,
  discountLabels,
} from './fields';
import DiscountTypeSelect from './components/DiscountTypeSelect';
import DiscountSettings from './components/DiscountSettings';

interface Props {
  id: string | undefined;
  cloneDiscountId: string | undefined;
  initialData: DiscountTypes.ItemCreateParams;
  initialSelectsOptions:
    | ReturnType<typeof dataInitialSelectsOptions>
    | undefined;
  drawer?: boolean;
  drawerTitle?: string;
  selectedShop: ShopTypes.ItemData | undefined;
  setSelectedShop: (value?: ShopTypes.ItemData) => void;
}

const Form: React.FC<Props> = (props: Props): JSX.Element => {
  const {
    initialData,
    id,
    initialSelectsOptions,
    drawer,
    drawerTitle,
    setSelectedShop,
    selectedShop,
    cloneDiscountId,
  } = props;
  const navigate = useNavigate();
  const {
    state: { refetchTableData },
  } = useStoreContext();

  const {
    control,
    handleSubmit,
    watch,
    formState: { isDirty, isSubmitting },
    setValue,
  } = useForm<DiscountTypes.ItemCreateParams>({
    defaultValues: initialData,
  });

  const { updateFiltersSearchParams } = useFiltersSearchParams();

  const { requestFn } = useApiRequest(
    id ? (data) => discounts.update(id, data) : discounts.create,
  );

  const onSubmit = useCallback(
    async (data: DiscountTypes.ItemCreateParams) => {
      const values = id
        ? getDiffFields(initialData, data)
        : getFieldsWithValues(data);

      const res = await requestFn({
        args: values,
        successMessage: `Discount ${id ? 'updated' : 'created'}`,
        error: {
          code: 422,
          getError: (error) => error.message || 'Discount code already exists',
        },
      });

      if (res) {
        if (drawer) {
          updateFiltersSearchParams({
            discountId: undefined,
            drawerMode: undefined,
          });

          if (refetchTableData) {
            await refetchTableData();
          }
        } else {
          navigate(appLinks.discounts.details.as(res.discount_id), {
            replace: true,
            state: {
              from: id ? 'edit' : 'create',
            },
          });
        }
      }
    },
    [
      id,
      initialData,
      requestFn,
      drawer,
      updateFiltersSearchParams,
      refetchTableData,
      navigate,
    ],
  );

  const { data: discountTypes, isLoading } = useQuery(
    [discounts.endpoints.getDiscountTypes, selectedShop?._id.$oid],
    () => discounts.getDiscountTypes(selectedShop?._id.$oid || ''),
    {
      enabled: !!selectedShop?._id.$oid,
      // keepPreviousData: true,
      refetchOnMount: false,
    },
  );

  const code = watch('coupon_code');
  const value = watch('value');
  const appDescription = watch('app_description');
  const appFunctionId = watch('app_function_id');
  const brand = watch('brand');
  const countryTeam = watch('country_team');

  const isDiscountDisabled = checkExternalDiscount(code).hasMatch && value > 0;

  const [activeStep, setActiveStep] = useState(selectedShop ? 1 : 0);
  const [selectedDiscount, setSelectedDiscount] = useState<
    DiscountTypes.DiscountTypeItem | undefined
  >();

  const handleStep = (step: number) => () => {
    setActiveStep(step);
  };

  const steps = [
    'Select shop',
    'Select discount type',
    'Fill in discount details',
  ];

  useEffect(() => {
    if (
      (id || cloneDiscountId) &&
      discountTypes &&
      !isLoading &&
      !selectedDiscount
    ) {
      if (appFunctionId) {
        setSelectedDiscount(
          discountTypes.find((d) => d.app_function_id === appFunctionId),
        );
      }

      setActiveStep(2);
    }
  }, [
    appFunctionId,
    discountTypes,
    id,
    isLoading,
    selectedDiscount,
    cloneDiscountId,
  ]);

  const getStepLabel = (step: string) => {
    if (activeStep === 1) return step;
    if (isLoading) return 'Loading...';

    if (selectedDiscount) {
      if (selectedDiscount.namespace in discountLabels) {
        return discountLabels[selectedDiscount.namespace];
      }
      return selectedDiscount.app_description;
    }

    return 'Default discount';
  };

  return (
    <FormContainer
      variant={drawer ? 'drawer' : 'page'}
      drawerTitle={drawerTitle}
      pageLink={id ? appLinks.discounts.edit.as(id) : undefined}
      onSubmit={handleSubmit(onSubmit)}
      loading={isSubmitting}
      disabled={isDiscountDisabled || (!!id && !isDirty)}
    >
      <Stepper nonLinear activeStep={activeStep} orientation="vertical">
        {steps.map((step, index) => (
          <Step key={step}>
            <StepButton
              onClick={handleStep(index)}
              disabled={id ? index === 1 : false}
            >
              {index === 0 &&
                (activeStep === 0
                  ? step
                  : `${selectedShop?.name}, ${brand}, ${countryTeam}`)}
              {index === 1 && getStepLabel(step)}
              {index === 2 && step}
            </StepButton>
            <StepContent>
              {index === 0 && (
                <GeneralSettings
                  id={id}
                  control={control}
                  setValue={setValue}
                  setSelectedShop={(v) => {
                    setSelectedShop(v);
                    setSelectedDiscount(undefined);
                  }}
                />
              )}
              {index === 1 && (
                <DiscountTypeSelect
                  discountTypes={discountTypes}
                  watch={watch}
                  setValue={setValue}
                  onChange={(value) => {
                    setActiveStep(2);
                    setSelectedDiscount(value);
                  }}
                />
              )}
              {index === 2 && (
                <DiscountSettings
                  control={control}
                  setValue={setValue}
                  watch={watch}
                  initialData={initialData}
                  initialSelectsOptions={initialSelectsOptions}
                  selectedDiscount={selectedDiscount}
                />
              )}
            </StepContent>
          </Step>
        ))}
      </Stepper>
      {activeStep !== 2 && (
        <Button
          fullWidth
          sx={{ my: 2 }}
          onClick={() => {
            setActiveStep((p) => {
              if (id) {
                return p === 0 ? 2 : 0;
              }
              return p + 1;
            });
          }}
          disabled={!!(id && appDescription && !selectedDiscount)}
        >
          Continue
        </Button>
      )}
    </FormContainer>
  );
};

export default Form;
